// Deal Products / Line Items panel. Add products from the catalog, set qty/price/discount,
// total auto-computes. Optional one-click sync of the line-item subtotal to deal.valueUsd.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Trash2, RefreshCw } from "lucide-react";

interface Props {
  dealId: string;
  /** Invalidate the deal query so the headline Amount card updates after sync. */
  dealQueryKey?: unknown[];
}

export function DealProductsPanel({ dealId, dealQueryKey }: Props) {
  const qc = useQueryClient();
  const queryKey = ["/deals", dealId, "products"];
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/deals/${dealId}/products`)).data as { data: any[]; subtotal: number },
  });

  const rows = data?.data ?? [];
  const subtotal = data?.subtotal ?? 0;

  async function updateLine(lineId: string, patch: Record<string, unknown>) {
    await api.put(`/deals/${dealId}/products/${lineId}`, patch);
    qc.invalidateQueries({ queryKey });
  }

  async function remove(lineId: string) {
    if (!confirm("Remove this line item?")) return;
    await api.delete(`/deals/${dealId}/products/${lineId}`);
    qc.invalidateQueries({ queryKey });
  }

  const syncMut = useMutation({
    mutationFn: async () => (await api.post(`/deals/${dealId}/products/sync-amount`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      if (dealQueryKey) qc.invalidateQueries({ queryKey: dealQueryKey });
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm">
          <span className="text-muted-foreground">Line items: </span>
          <span className="font-medium">{rows.length}</span>
          <span className="ml-3 text-muted-foreground">Subtotal: </span>
          <span className="font-semibold tabular-nums">${Number(subtotal).toLocaleString()}</span>
        </div>
        <div className="flex gap-2">
          {rows.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
              title="Set the deal's headline Amount to this subtotal"
            >
              <RefreshCw className="mr-1 h-4 w-4"/>Sync to deal Amount
            </Button>
          )}
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="mr-1 h-4 w-4"/>Add product
          </Button>
        </div>
      </div>

      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No line items yet. Add products from the catalog to build a quote.
        </p>
      )}

      {rows.length > 0 && (
        <div className="rounded-md border">
          <Table>
            <Thead>
              <Tr>
                <Th>Product</Th>
                <Th className="w-24">Qty</Th>
                <Th className="w-32">Unit price</Th>
                <Th className="w-24">Disc %</Th>
                <Th className="w-28 text-right">Line total</Th>
                <Th className="w-10"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={r.id} className="group">
                  <Td>
                    <div className="font-medium">{r.product?.name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.product?.code && <span className="font-mono mr-2">{r.product.code}</span>}
                      {r.product?.usageUnit && <span>per {r.product.usageUnit}</span>}
                      {r.listPrice && Number(r.unitPrice) !== Number(r.listPrice) && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">
                          List: {r.product?.currency ?? "USD"} {Number(r.listPrice).toLocaleString()}
                        </Badge>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={r.quantity}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== Number(r.quantity)) updateLine(r.id, { quantity: v });
                      }}
                      className="h-7 text-sm"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={r.unitPrice}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== Number(r.unitPrice)) updateLine(r.id, { unitPrice: v });
                      }}
                      className="h-7 text-sm"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      defaultValue={r.discountPct ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? null : Number(e.target.value);
                        if ((v ?? null) !== (r.discountPct ? Number(r.discountPct) : null)) updateLine(r.id, { discountPct: v });
                      }}
                      className="h-7 text-sm"
                    />
                  </Td>
                  <Td className="text-right tabular-nums font-medium">
                    ${Number(r.totalPrice).toLocaleString()}
                  </Td>
                  <Td>
                    <button
                      onClick={() => remove(r.id)}
                      className="opacity-0 group-hover:opacity-100 text-destructive"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-4 w-4"/>
                    </button>
                  </Td>
                </Tr>
              ))}
              <Tr>
                <Td colSpan={4} className="text-right text-sm font-medium">Subtotal</Td>
                <Td className="text-right tabular-nums font-bold">${Number(subtotal).toLocaleString()}</Td>
                <Td></Td>
              </Tr>
            </Tbody>
          </Table>
        </div>
      )}

      <AddLineItemModal open={addOpen} onClose={() => setAddOpen(false)} dealId={dealId} />
    </div>
  );
}

function AddLineItemModal({ open, onClose, dealId }: { open: boolean; onClose: () => void; dealId: string }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["picker-products", q],
    queryFn: async () => (await api.get("/products", { params: { q, pageSize: 25, isActive: true } })).data.data as any[],
    enabled: open,
  });

  const rows = useMemo(() => data ?? [], [data]);
  const selected = rows.find((p) => p.id === productId);

  // Auto-fill unit price from product.list price when product changes.
  useEffect(() => {
    if (selected && !unitPrice) setUnitPrice(String(selected.unitPrice ?? ""));
  }, [selected]);

  const total = useMemo(() => {
    const q = Number(quantity) || 0;
    const u = Number(unitPrice) || 0;
    const d = Number(discountPct) || 0;
    return q * u * (1 - d / 100);
  }, [quantity, unitPrice, discountPct]);

  const mut = useMutation({
    mutationFn: async () => {
      await api.post(`/deals/${dealId}/products`, {
        productId,
        quantity: Number(quantity),
        unitPrice: unitPrice === "" ? undefined : Number(unitPrice),
        discountPct: discountPct === "" ? undefined : Number(discountPct),
        description: description || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/deals", dealId, "products"] });
      setProductId(""); setQuantity("1"); setUnitPrice(""); setDiscountPct(""); setDescription(""); setQ(""); onClose();
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to add line item."),
  });

  return (
    <BulkModal
      open={open}
      onClose={() => { setProductId(""); setError(null); onClose(); }}
      title="Add line item"
      description="Pick a product, set quantity and price, see the line total."
      confirmLabel="Add line"
      confirmDisabled={!productId || !quantity || !unitPrice}
      busy={mut.isPending}
      onConfirm={() => mut.mutate()}
      error={error}
    >
      <div>
        <Label>Search products</Label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a product name or SKU…" />
      </div>
      <div className="max-h-48 overflow-auto rounded-md border">
        {isFetching && <p className="p-3 text-sm text-muted-foreground">Loading…</p>}
        {!isFetching && rows.length === 0 && <p className="p-3 text-sm text-muted-foreground">No products.</p>}
        {rows.map((p) => (
          <label key={p.id} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-muted">
            <input
              type="radio"
              name="product"
              checked={productId === p.id}
              onChange={() => { setProductId(p.id); setUnitPrice(String(p.unitPrice ?? "")); }}
            />
            <span className="font-medium">{p.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {p.unitPrice ? `${p.currency ?? "USD"} ${Number(p.unitPrice).toLocaleString()}` : "no list price"}
              {p.usageUnit ? ` / ${p.usageUnit}` : ""}
            </span>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label htmlFor="qty">Quantity</Label>
          <Input id="qty" type="number" min="0" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="up">Unit price</Label>
          <Input id="up" type="number" min="0" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="dp">Discount %</Label>
          <Input id="dp" type="number" min="0" max="100" step="0.1" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
        </div>
      </div>
      <div className="rounded-md bg-muted/40 p-3 text-sm">
        <span className="text-muted-foreground">Line total: </span>
        <span className="font-semibold tabular-nums">${Math.round(total).toLocaleString()}</span>
      </div>
      <div>
        <Label htmlFor="desc">Description (optional)</Label>
        <Input id="desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
    </BulkModal>
  );
}
